import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useVibesites,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// FAQ item shape (matches faqs[*] in @vibesites/schema)
// ============================================================================

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

// Normalise a category string so trivial variations ("General", "general ",
// "GENERAL") still group together.
function normalizeCategory(raw: unknown): string {
  return (raw == null ? '' : String(raw)).trim();
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '';
}

/**
 * Partition the FAQ list into category groups while preserving first-seen
 * order (so authored ordering wins over alphabetisation). FAQs with no
 * category fall into a trailing "General Questions" bucket so they still
 * render; when NO item has a category, the whole list is returned as a
 * single unnamed group and the component renders it ungrouped.
 */
interface Group {
  key: string;
  /** Visible heading; empty string means "render ungrouped". */
  label: string;
  items: FAQ[];
}

function groupFaqs(items: FAQ[]): { groups: Group[]; grouped: boolean } {
  const anyCategorised = items.some((item) => hasValue(normalizeCategory(item.category)));
  if (!anyCategorised) {
    return {
      groups: [{ key: '__all__', label: '', items }],
      grouped: false,
    };
  }

  const byKey = new Map<string, Group>();
  const uncategorised: FAQ[] = [];

  for (const item of items) {
    const label = normalizeCategory(item.category);
    if (!label) {
      uncategorised.push(item);
      continue;
    }
    const key = label.toLowerCase();
    let group = byKey.get(key);
    if (!group) {
      group = { key, label, items: [] };
      byKey.set(key, group);
    }
    group.items.push(item);
  }

  const groups: Group[] = Array.from(byKey.values());
  if (uncategorised.length) {
    groups.push({ key: '__general__', label: 'General Questions', items: uncategorised });
  }
  return { groups, grouped: true };
}

// ============================================================================
// Component
// ============================================================================

export function FAQList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const { property } = useVibesites();

  const items = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  // Resolve richtext answers (handles embedded <span data-type="field-token">
  // tokens like property_name) up-front so the render tree stays declarative.
  const safeItems = items ?? [];
  const { groups, grouped } = useMemo(() => groupFaqs(safeItems), [safeItems]);
  const resolvedAnswers = useMemo(
    () => safeItems.map((item) => resolveRichText(item.answer ?? '', property as Record<string, any>)),
    [safeItems, property],
  );
  // Build a stable identity → resolved HTML lookup so the per-group render
  // below can read each FAQ's resolved answer without re-resolving.
  const answerByRef = useMemo(() => {
    const map = new WeakMap<FAQ, string>();
    safeItems.forEach((item, i) => map.set(item, resolvedAnswers[i]));
    return map;
  }, [safeItems, resolvedAnswers]);

  const hasAny = safeItems.length > 0;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pb-16 md:pb-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {hasAny ? (
            <div className="flex flex-col gap-10">
              {groups.map((group) => (
                <div key={group.key}>
                  {grouped && group.label ? (
                    <h3 className="faq-group-heading font-heading text-xl sm:text-2xl font-semibold uppercase tracking-wider text-[var(--vibe-color-primary)] mb-4 pb-2 border-b border-[var(--vibe-color-border)]">
                      {group.label}
                    </h3>
                  ) : null}

                  <div className="flex flex-col divide-y divide-[var(--vibe-color-border)] border-y border-[var(--vibe-color-border)]">
                    {group.items.map((item, i) => {
                      const question = hasValue(item.question) ? item.question! : 'Question';
                      const answerHtml = answerByRef.get(item) ?? '';
                      return (
                        <details key={`${group.key}-${i}`} className="faq-item group">
                          <summary className="faq-summary">
                            <span className="faq-question font-heading text-base sm:text-lg font-medium text-[var(--vibe-color-text)]">
                              {question}
                            </span>
                            <span className="faq-chevron" aria-hidden="true">
                              <svg viewBox="0 0 20 20" fill="none" width="20" height="20">
                                <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                          </summary>
                          {answerHtml ? (
                            <div
                              className="faq-answer font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
                              dangerouslySetInnerHTML={{ __html: answerHtml }}
                            />
                          ) : null}
                        </details>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Frequently asked questions will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-item {
          padding: 0;
        }
        .faq-summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.1rem 0.25rem;
          cursor: pointer;
          list-style: none;
          user-select: none;
        }
        .faq-summary::-webkit-details-marker { display: none; }

        .faq-question {
          flex: 1 1 auto;
          letter-spacing: 0.02em;
          text-align: left;
        }

        .faq-chevron {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .faq-item[open] .faq-chevron {
          transform: rotate(180deg);
        }

        .faq-summary:hover .faq-question {
          color: var(--vibe-color-primary);
        }

        .faq-answer {
          padding: 0 0.25rem 1.25rem;
        }
        .faq-answer p { margin: 0 0 0.75rem; }
        .faq-answer p:last-child { margin-bottom: 0; }
        .faq-answer ul { list-style: disc; padding-left: 1.25rem; margin: 0 0 0.75rem; }
        .faq-answer ol { list-style: decimal; padding-left: 1.25rem; margin: 0 0 0.75rem; }
        .faq-answer li { margin-bottom: 0.25rem; }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* Mobile: center the category heading so each group reads as a
           distinct, centered label above its accordion. Row questions keep
           their left-aligned treatment so the chevron pairing stays natural. */
        @media (max-width: 640px) {
          .faq-group-heading { text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-chevron { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
